import React, { useState } from 'react';
import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash, FaDownload } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import Paginate from '../../components/Paginate';
import {
  useGetIncomesQuery,
  useDeleteIncomeMutation,
  useCreateIncomeMutation,
} from '../../slices/incomesApiSlice';
import { toast } from 'react-toastify';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const NetCash = () => {
  const { pageNumber } = useParams() || 1;

  const { data, isLoading, error, refetch } = useGetIncomesQuery({ pageNumber });
  const [deleteIncome, { isLoading: loadingDelete }] = useDeleteIncomeMutation();
  const [createIncome, { isLoading: loadingCreate }] = useCreateIncomeMutation();

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Create a new income
  const createIncomeHandler = async () => {
    if (window.confirm('Are you sure you want to create a new income?')) {
      try {
        const newIncome = {
          date: new Date().toISOString().split('T')[0],
          account: 'Debit',
          accountName: 'Default Account',
          subAccount: 'Default Sub Account',
          details: 'New Income',
          amount: 0,
          status: 'Pending',
          category: 'Other',
          notes: 'Default note',
        
        };
        await createIncome(newIncome).unwrap();
        toast.success('New income created successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Delete an income
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this income?')) {
      try {
        await deleteIncome(id).unwrap();
        toast.success('Income deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  // Filter and sort incomes
  const filterAndSortIncomes = () => {
    let filteredIncomes = [...(data?.incomes || [])];

    // Filter by text input
    if (filter) {
      filteredIncomes = filteredIncomes.filter(
        (income) =>
          income.account?.toLowerCase().includes(filter.toLowerCase()) ||
          income.accountName?.toLowerCase().includes(filter.toLowerCase()) ||
          income.details?.toLowerCase().includes(filter.toLowerCase()) ||
          income.category?.toLowerCase().includes(filter.toLowerCase()) ||
          income.subAccount?.toLowerCase().includes(filter.toLowerCase()) ||
          income.notes?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Filter by date range
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filteredIncomes = filteredIncomes.filter((income) => {
        const incomeDate = new Date(income.date);
        return incomeDate >= start && incomeDate <= end;
      });
    }

    // Sort incomes
    if (sortField) {
      filteredIncomes.sort((a, b) => {
        if (sortField === 'amount') {
          return sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount;
        } else if (sortField === 'date') {
          return sortOrder === 'asc'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else {
          const aValue = a[sortField]?.toLowerCase() || '';
          const bValue = b[sortField]?.toLowerCase() || '';
          return sortOrder === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
      });
    }

    return filteredIncomes;
  };

  // Calculate total for date range
  const calculateTotalForDateRange = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);

    const filteredIncomes = data?.incomes?.filter((income) => {
      const incomeDate = new Date(income.date);
      return incomeDate >= start && incomeDate <= end;
    }) || [];

    return filteredIncomes.reduce((acc, income) => acc + income.amount, 0);
  };

  // Calculate total for all incomes
  const totalAmount = data?.incomes?.reduce((acc, income) => acc + income.amount, 0) || 0;
    const downloadIncomeImages = async (income) => {
            const zip = new JSZip();
            const folder = zip.folder(`Income_${income._id}`);
        
            const imagePromises = income.images.map(async (imagePath, index) => {
              try {
                const response = await fetch(imagePath.replace(/\\/g, '/')); // Fix path formatting
                const blob = await response.blob();
                const urlParts = imagePath.split('/');
                const filename = urlParts[urlParts.length - 1]; // Use original filename
        
                folder.file(filename, blob);
              } catch (error) {
                console.error(`Failed to fetch ${imagePath}`, error);
              }
            });
        
            await Promise.all(imagePromises);
            zip.generateAsync({ type: 'blob' }).then((zipFile) => {
              saveAs(zipFile, `Income_${income._id}_Images.zip`);
            });
          };
        
          const downloadAllImages = async () => {
            const zip = new JSZip();
            const folder = zip.folder('All_Incomes_Images');
        
            const imagePromises = data.incomes.flatMap((income) =>
              income.images.map(async (imagePath, index) => {
                try {
                  const response = await fetch(imagePath.replace(/\\/g, '/')); // Fix path formatting
                  const blob = await response.blob();
                  const urlParts = imagePath.split('/');
                  const filename = urlParts[urlParts.length - 1];
        
                  folder.file(filename, blob); // Unique naming per income
                } catch (error) {
                  console.error(`Failed to fetch ${imagePath}`, error);
                }
              })
            );
        
            await Promise.all(imagePromises);
            zip.generateAsync({ type: 'blob' }).then((zipFile) => {
              saveAs(zipFile, 'All_Incomes_Images.zip');
            });
          };
      
          

const exportToExcel = () => {
  const incomes = filterAndSortIncomes();

  const worksheetData = incomes.map((income) => ({
    IncomeId: income._id,
    Date: new Date(income.date).toLocaleDateString(),
    Account: income.account,
    AccountName: income.accountName,
    Details: income.details,
    Amount: income.amount,
    Category: income.category,
    Notes: income.notes,
    Images: income.images[0],
  }));

  const worksheet = XLSX.utils.json_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Incomes');

  XLSX.writeFile(workbook, 'Cash_Collected.xlsx');
};
  
  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Cash Collected</h1>
        </Col>
        <Col className="text-end">
          <Button className="my-3 mx-2" onClick={downloadAllImages}>
              <FaDownload /> Download All Images
          </Button>
          <Link to='/create/income' className='btn btn-light my-3'>
            Add Data
          </Link>
          <Button onClick={createIncomeHandler} className="my-3" style={{display: "none"}}>
            <FaPlus /> Add New Income
          </Button>
          <Button className="my-3 mx-2" onClick={exportToExcel}>
            <FaDownload /> Export to Excel
           </Button>
           <Link to='/update/income/excelform' className='btn btn-light my-3'>
              BulkUpdate
            </Link>
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col>
          <h5>Total Amount: ${totalAmount.toFixed(2)}</h5>
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by account, name, details, category, subAccount, or notes"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
        </Col>
        <Col md={4}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </Col>
        <Col md={4}>
          <h5>
            Total (Filtered Range): ${calculateTotalForDateRange().toFixed(2)}
          </h5>
        </Col>
      </Row>
  
      {loadingCreate && <Loader />}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'An error occurred'}</Message>
      ) : data?.incomes?.length > 0 ? (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                  Date {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>Account</th>
                <th onClick={() => handleSort('accountName')} style={{ cursor: 'pointer' }}>
                  Account Name {sortField === 'accountName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('details')} style={{ cursor: 'pointer' }}>
                  Details {sortField === 'details' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer' }}>
                  Amount {sortField === 'amount' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                  Category {sortField === 'category' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('notes')} style={{ cursor: 'pointer' }}>
                  Notes {sortField === 'notes' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortIncomes().map((income) => (
                <tr key={income._id}>
                  <td>{new Date(income.date).toLocaleDateString()}</td>
                  <td>{income.account}</td>
                  <td>{income.accountName}</td>
                  <td>{income.details}</td>
                  <td>
                    {income.amount >= 0
                      ? `$${income.amount.toFixed(2)}`
                      : `-$${Math.abs(income.amount).toFixed(2)}`}
                  </td>
                  <td>{income.category}</td>
                  <td>{income.notes}</td>
                  <td className="thumbnails">
                    {income.images?.length > 0 && (
                      <img
                        src={income.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                          width: '15px',
                          height: '15px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </td>

                  <td>
                    <Link
                     className="btn btn-primary btn-sm m-2"
                    to={`/income/${income._id}`}
                    role="button"
                    >
                       Details
                    </Link>
                    <Button
                      as={Link}
                      to={`/admin/income/${income._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button variant="info" className="btn-sm" onClick={() => downloadIncomeImages(income)}>
                          <FaDownload /> Images
                      </Button>
                    <Button
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(income._id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Paginate pages={data.pages} page={data.page} isAdmin={true} type="netcash" />
        </>
      ) : (
        <Message variant="info">No incomes found</Message>
      )}
    </>
  );
};

export default NetCash;